import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, AlertCircle } from 'lucide-react';
import CryptoJS from 'crypto-js';
import logo from '../../assets/logo.png';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

/**
 * Parse WDDX (ColdFusion XML format) to JavaScript object
 * Handles the simplified STRUCT format returned by ColdFusion
 */
const parseWDDX = (wddxString) => {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(wddxString, 'text/xml');

        const parseNode = (node) => {
            if (!node) return null;

            const nodeName = node.nodeName.toUpperCase();

            if (nodeName === 'STRUCT') {
                const obj = {};
                const entries = node.querySelectorAll(':scope > ENTRY');
                entries.forEach(entry => {
                    const name = entry.getAttribute('NAME');
                    const type = entry.getAttribute('TYPE');
                    const childStruct = entry.querySelector('STRUCT');

                    if (childStruct) {
                        obj[name] = parseNode(childStruct);
                    } else if (type === 'NUMBER') {
                        obj[name] = parseFloat(entry.textContent);
                    } else if (type === 'STRING') {
                        const val = entry.textContent;
                        // Convert string "true"/"false" to boolean
                        if (val === 'true') obj[name] = true;
                        else if (val === 'false') obj[name] = false;
                        else obj[name] = val;
                    } else {
                        obj[name] = entry.textContent;
                    }
                });
                return obj;
            }

            return null;
        };

        const rootStruct = xmlDoc.querySelector('STRUCT');
        return parseNode(rootStruct);
    } catch (e) {
        console.error('Error parsing WDDX:', e);
        return null;
    }
};

/**
 * Login page component with email/code authentication and OAuth
 * @param {Object} props
 * @param {Function} props.onBack - Handler to navigate back
 * @param {string} props.language - Current language
 */
const LoginPage = ({ onBack, language, initialMode = 'login' }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [isLoginMode, setIsLoginMode] = useState(initialMode === 'login');
    const [isForgotMode, setIsForgotMode] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const t = {
        fr: {
            title: 'Connexion',
            loginTitle: 'Se connecter',
            registerTitle: 'Créer un compte',
            forgotTitle: 'Code oublié',
            email: 'Adresse e-mail',
            code: 'Code',
            login: 'Se connecter',
            register: "S'inscrire",
            forgotCode: 'Code oublié ?',
            sendCode: 'Envoyer un nouveau code',
            backToLogin: 'Retour à la connexion',
            orContinueWith: 'Ou continuer avec',
            googleLogin: 'Connexion avec Google',
            appleLogin: 'Connexion avec Apple',
            noAccount: "Pas encore de compte ?",
            hasAccount: 'Déjà un compte ?',
            createAccount: 'Créer un compte',
            signIn: 'Se connecter',
            codeSent: 'Un nouveau code a été envoyé à votre adresse e-mail',
            invalidEmail: 'Adresse e-mail invalide',
            fillFields: 'Veuillez remplir tous les champs',
        },
        en: {
            title: 'Login',
            loginTitle: 'Sign In',
            registerTitle: 'Create Account',
            forgotTitle: 'Forgot Code',
            email: 'Email Address',
            code: 'Code',
            login: 'Login',
            register: 'Register',
            forgotCode: 'Forgot code?',
            sendCode: 'Send new code',
            backToLogin: 'Back to login',
            orContinueWith: 'Or continue with',
            googleLogin: 'Sign in with Google',
            appleLogin: 'Sign in with Apple',
            noAccount: "Don't have an account?",
            hasAccount: 'Already have an account?',
            createAccount: 'Create account',
            signIn: 'Sign in',
            codeSent: 'A new code has been sent to your email address',
            invalidEmail: 'Invalid email address',
            fillFields: 'Please fill in all fields',
        }
    };

    const texts = t[language] || t.fr;

    const validateEmail = (email) => {
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateEmail(email)) {
            setError(texts.invalidEmail);
            return;
        }

        if (!isForgotMode && !code) {
            setError(texts.fillFields);
            return;
        }

        setLoading(true);

        try {
            if (isForgotMode) {
                // TODO: Implement forgot password functionality
                setError(language === 'fr'
                    ? 'Fonctionnalité "Code oublié" à venir. Contactez un administrateur.'
                    : 'Forgot password feature coming soon. Contact an administrator.');
                setLoading(false);
                return;
            }

            if (!isLoginMode) {
                // TODO: Implement registration functionality
                setError(language === 'fr'
                    ? 'Fonctionnalité "Créer un compte" à venir. Contactez un administrateur.'
                    : 'Registration feature coming soon. Contact an administrator.');
                setLoading(false);
                return;
            }

            // Hash the code with SHA-256 before sending
            const hashedPassword = CryptoJS.SHA256(code).toString();

            // Use the login endpoint that returns a JWT token
            const response = await fetch('https://apilinkoo.creago.be/rest/linkoo/peoples/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    password: hashedPassword
                }),
            });

            // Try to parse JSON response
            let data;
            const text = await response.text();
            console.log('Login API raw response:', text);

            if (!text || text.trim() === '') {
                throw new Error(language === 'fr'
                    ? 'Réponse vide du serveur'
                    : 'Empty server response');
            }

            // Check if response looks like XML/WDDX instead of JSON
            if (text.trim().startsWith('<')) {
                console.log('Server returned WDDX, parsing...');
                // Parse WDDX format from ColdFusion
                data = parseWDDX(text);
                if (!data) {
                    throw new Error(language === 'fr'
                        ? 'Erreur serveur: Impossible de parser la réponse WDDX'
                        : 'Server error: Unable to parse WDDX response');
                }
            } else {
                try {
                    data = JSON.parse(text);
                } catch (parseError) {
                    console.error('JSON parse error:', parseError, 'Raw text:', text);
                    throw new Error(language === 'fr'
                        ? `Erreur serveur: Impossible de décoder la réponse`
                        : `Server error: Unable to parse response`);
                }
            }

            // Handle both lowercase and uppercase JSON keys (ColdFusion returns uppercase)
            const success = data.success || data.SUCCESS;
            const message = data.message || data.MESSAGE;
            const responseData = data.data || data.DATA;

            if (success) {
                // Login endpoint returns {token: "...", user: {...}}
                const token = responseData.token || responseData.TOKEN;
                const userData = responseData.user || responseData.USER || responseData;

                // Login success - store user info and redirect
                const firstName = userData.firstName || userData.FIRSTNAME || userData.firstname || '';
                const lastName = userData.lastName || userData.LASTNAME || userData.lastname || '';
                const userEmail = userData.email || userData.EMAIL || email;
                const userId = userData.id || userData.ID || '';
                const userUuid = userData.peopleUuid || userData.PEOPLEUUID || userData.peopleuuid || '';
                const storageKey = userData.storageKey || userData.STORAGEKEY || userData.storagekey || '';
                const userRole = userData.role || userData.ROLE || '';

                console.log('Login responseData:', responseData);
                console.log('Login token:', token ? token.substring(0, 20) + '...' : 'MISSING');
                console.log('Login userData:', userData);
                console.log('Extracted userUuid:', userUuid);
                console.log('Extracted storageKey:', storageKey ? 'present' : 'MISSING');
                console.log('Extracted role:', userRole);

                localStorage.setItem('authToken', token || 'user-authenticated');
                localStorage.setItem('userEmail', userEmail);
                localStorage.setItem('userName', `${firstName} ${lastName}`.trim());
                localStorage.setItem('userId', userId.toString());
                localStorage.setItem('userUuid', userUuid);
                localStorage.setItem('userRole', userRole);

                // Save full user object for contacts sync
                localStorage.setItem('linkoo_user', JSON.stringify({
                    id: userId,
                    email: userEmail,
                    firstName: firstName,
                    lastName: lastName,
                    peopleUuid: userUuid,
                    storageKey: storageKey,
                    role: userRole
                }));

                setIsRedirecting(true);
                setTimeout(() => {
                    window.location.href = '/';
                }, 1500);
            } else {
                setError(message || (language === 'fr' ? 'Identifiants incorrects' : 'Invalid credentials'));
            }
        } catch (err) {
            setError(err.message || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        setLoading(true);

        try {
            // Redirect to Google OAuth
            window.location.href = 'https://apilinkoo.creago.be/api/rest/userAuth/google';
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleAppleLogin = async () => {
        setError('');
        setLoading(true);

        try {
            // Redirect to Apple OAuth
            window.location.href = 'https://apilinkoo.creago.be/api/rest/userAuth/apple';
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    // Show loading screen when redirecting after successful login
    if (isRedirecting) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="relative w-24 h-24 mx-auto mb-6">
                        {/* Outer spinning ring */}
                        <div className="absolute inset-0 rounded-full border-4 border-linkoo-purple/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-linkoo-purple animate-spin"></div>
                        {/* Inner spinning ring (opposite direction) */}
                        <div className="absolute inset-3 rounded-full border-4 border-linkoo-pink/20"></div>
                        <div className="absolute inset-3 rounded-full border-4 border-transparent border-t-linkoo-pink animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                        {/* Center dot */}
                        <div className="absolute inset-8 rounded-full bg-gradient-to-br from-linkoo-purple to-linkoo-pink animate-pulse"></div>
                    </div>
                    <p className="text-lg text-gray-600 font-medium">
                        {language === 'fr' ? 'Connexion en cours...' : 'Logging in...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                {/* Back button */}
                <button
                    onClick={onBack}
                    className="mb-6 flex items-center gap-2 text-linkoo-purple hover:text-linkoo-pink transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    {language === 'fr' ? 'Retour' : 'Back'}
                </button>

                {/* Login card */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <img src={logo} alt="Linkoo Logo" className="h-20 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-linkoo-purple">
                            {isForgotMode ? texts.forgotTitle : isLoginMode ? texts.loginTitle : texts.registerTitle}
                        </h1>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <p className="text-red-800 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Login form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                {texts.email}
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-linkoo-purple"
                                    placeholder="exemple@email.com"
                                    required
                                />
                            </div>
                        </div>

                        {/* Code field (not shown in forgot mode) */}
                        {!isForgotMode && (
                            <div>
                                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                                    {texts.code}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        id="code"
                                        type="password"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-linkoo-purple"
                                        placeholder="••••••"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Forgot code link (only in login mode) */}
                        {isLoginMode && !isForgotMode && (
                            <div className="text-right">
                                <button
                                    type="button"
                                    onClick={() => setIsForgotMode(true)}
                                    className="text-sm text-linkoo-purple hover:text-linkoo-pink transition-colors"
                                >
                                    {texts.forgotCode}
                                </button>
                            </div>
                        )}

                        {/* Submit button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    {language === 'fr' ? 'Chargement...' : 'Loading...'}
                                </span>
                            ) : (
                                isForgotMode ? texts.sendCode : isLoginMode ? texts.login : texts.register
                            )}
                        </button>
                    </form>

                    {/* Back to login button (in forgot mode) */}
                    {isForgotMode && (
                        <div className="mt-4">
                            <button
                                onClick={() => setIsForgotMode(false)}
                                className="w-full text-center text-linkoo-purple hover:text-linkoo-pink transition-colors"
                            >
                                {texts.backToLogin}
                            </button>
                        </div>
                    )}

                    {/* OAuth section (not shown in forgot mode) */}
                    {!isForgotMode && (
                        <>
                            <div className="my-6 flex items-center gap-4">
                                <div className="flex-1 h-px bg-gray-300"></div>
                                <span className="text-sm text-gray-500">{texts.orContinueWith}</span>
                                <div className="flex-1 h-px bg-gray-300"></div>
                            </div>

                            <div className="space-y-3">
                                {/* Google login button */}
                                <button
                                    onClick={handleGoogleLogin}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    <span className="font-medium text-gray-700">{texts.googleLogin}</span>
                                </button>

                                {/* Apple login button - DÉSACTIVÉ TEMPORAIREMENT */}
                                {/* <button
                                    onClick={handleAppleLogin}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                    </svg>
                                    <span className="font-medium text-gray-700">{texts.appleLogin}</span>
                                </button> */}
                            </div>
                        </>
                    )}

                    {/* Link to register page (hidden if registration is disabled) */}
                    {!isRegistrationDisabled && !isForgotMode && isLoginMode && (
                        <div className="mt-6 text-center text-sm text-gray-600">
                            {texts.noAccount}
                            {' '}
                            <button
                                onClick={() => navigate('/register')}
                                className="text-linkoo-purple hover:text-linkoo-pink font-medium transition-colors"
                            >
                                {texts.createAccount}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
